import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { useNavigate } from '@app/hooks';
import { getPortfolio, selectLocation, selectUser } from '@app/redux';
import { AlertType, DisplayLocation, setAlerts } from '@app/redux/alerts';
import { getPurchaseRetentionOffers, selectRetentionOffers } from '@app/redux/disconnect';

import { NotificationLevel, RetentionOffer as RetentionOfferType } from '@mfe/shared/schema-types';

import RetentionOfferConfirmationModal from '../RetentionModals/ConfirmationModal';
import PurchaseModal from '../RetentionModals/PurchaseModal';
import Loading from './Loading';
import RetentionOffer from './RetentionOffer';

import styles from './index.module.scss';

const RetentionOffers = ({
  isModalOpen,
  closeModal,
  handleSelect,
}: {
  isModalOpen: boolean;
  closeModal: () => void;
  handleSelect: () => void;
}) => {
  const { goTo } = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation('Disconnect');

  const {
    user: { productInstanceId },
  } = useSelector(selectUser);
  const { data: retentionOffers, loading } = useSelector(selectRetentionOffers);
  const { coordinates, address } = useSelector(selectLocation) || {};
  const [selectedOffer, setSelectedOffer] = useState<Partial<RetentionOfferType> | null>(null);
  const { offer_id, id, name } = selectedOffer || {};
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const navigateToPlanTabAccount = () => {
    sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
    goTo('Profile');

    dispatch(
      setAlerts([
        {
          level: NotificationLevel.Success,
          title: `${t('retentionOffers.banner.title')}`,
          caption: `${t('retentionOffers.banner.description')}`,
          displayLocation: DisplayLocation.GLOBAL,
          type: AlertType.RetentionOfferSuccess,
        },
      ])
    );
    dispatch(getPortfolio());
  };

  const hasOffers = retentionOffers && retentionOffers?.length && retentionOffers?.length > 0;

  const handleSelectOffer = () => {
    if (!offer_id || !id || !productInstanceId) {
      console.error('Missing required fields for purchase retention offer');
      return;
    }
    dispatch(
      getPurchaseRetentionOffers({
        shoppingCartId: offer_id,
        productTypeId: id,
        rootProductTypeId: productInstanceId,
        coordinates: {
          latitude: coordinates?.latitude ?? 0,
          longitude: coordinates?.longitude ?? 0,
        },
        address: {
          addressLines: address?.addressLines ?? [],
          municipality: address?.municipality ?? '',
          region: address?.region ?? '',
          postalCode: address?.postalCode ?? '',
          countryCode: address?.countryCode ?? '',
        },
      })
    );

    setShowConfirmationModal(false);
  };
  return (
    <>
      {loading ? (
        <div className={styles['retentionOffers']}>
          <Loading />
          <Loading />
        </div>
      ) : hasOffers ? (
        <>
          <div>
            <Txt variant="bodyLargeBold">{t('retentionOffers.header')}</Txt>
            <Txt variant="bodySmallRegular" mt="8px">
              {t('retentionOffers.description')}
            </Txt>
          </div>
          <div className={styles['retentionOffers']}>
            {retentionOffers?.map((offer: Partial<RetentionOfferType>, index: number) => (
              <RetentionOffer
                key={index}
                name={offer.name}
                description={offer.description}
                handleSelect={() => {
                  handleSelect();
                  setSelectedOffer(offer);
                  setShowConfirmationModal(true);

                  pushToAdobeDataLayer({
                    event: DataLayerEventName.FORM_SUBMIT,
                    event_name: DataLayerEventName.FORM_SUBMIT,
                    event_label: `${AnalyticsEventNames.SelectedRetentionOffer} - ${offer.name}`,
                    event_category: Categories.Disconnect,
                  });
                }}
              />
            ))}
          </div>
        </>
      ) : null}
      {showConfirmationModal && (
        <RetentionOfferConfirmationModal
          isOpen={isModalOpen}
          retentionOfferName={name || ''}
          handleCancel={closeModal}
          handleContinue={handleSelectOffer}
        />
      )}
      <PurchaseModal
        isOpen={isModalOpen}
        handleCancel={closeModal}
        retentionOfferName={name || ''}
        handleContinue={navigateToPlanTabAccount}
      />
    </>
  );
};

export default RetentionOffers;
